import React, { useState, useRef, useCallback, useEffect } from 'react';
import type { Feature } from 'geojson';
import * as turf from '@turf/turf';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';

import Search from '@arcgis/core/widgets/Search';
import { HYBRID } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisTypes';
import { useLocalArcgisMapView } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useLocalArcgisMapView';
import { useMapSearchWidget } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useMapSearchWidget';
import { US_INITIAL_VIEW } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/constants';
import { useArcgisSketchLayer } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useArcgisSketchLayer';
import { useLocationGraphicLayer } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useLocationGraphicLayer';
import { zoomMapViewToGraphic } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/mapViewZoom';
import type { Geometry } from 'geojson';
import { LocationSummary } from './LocationSummary';
import { LatLngInputPair } from './LatLngInputPair';
import { useGeoJsonImport } from './useGeoJsonImport';
import {
  buildGeoLocationFromGeoJson,
  type GeoLocation,
} from './buildGeoLocationFromGeoJson';
import { useReverseGeocode } from './useReverseGeocode';
import { useLocationFromArcgisGeometry } from './useLocationFromArcgisGeometry';
import { MapToolPalette } from 'components/DesignSystems/MapToolPalette/MapToolPalette';
import {
  loadFemaRegionPolygons,
  type FemaRegionPolygon,
} from './femaRegionPolygons';

export type { GeoLocation };

interface GeoLocationComponentBaseProps {
  location: GeoLocation | undefined;
  isShowMarker?: boolean;
  markerColor?: string;
  hideSelectLocationButton?: boolean;
  readOnly?: boolean;
  /** AOR location only: offer the ten NOAA FEMA region polygons. */
  showFemaRegionMenu?: boolean;
}

interface GeoLocationComponentWithSetLocation
  extends GeoLocationComponentBaseProps {
  hideSelectLocationButton?: false;
  setLocation: (location: GeoLocation) => void;
  setWatchLocation?: (location: boolean) => void;
}

interface GeoLocationComponentWithoutSetLocation
  extends GeoLocationComponentBaseProps {
  hideSelectLocationButton: true;
  setWatchLocation?: (location: boolean) => void;
}

export type GeoLocationComponentProps =
  | GeoLocationComponentWithSetLocation
  | GeoLocationComponentWithoutSetLocation;

const GeoLocationComponent: React.FC<GeoLocationComponentProps> = ({
  location,
  isShowMarker: _isShowMarker = false,
  markerColor: _markerColor,
  hideSelectLocationButton = false,
  readOnly = false,
  showFemaRegionMenu = false,
  ...props
}) => {
  const setLocation = 'setLocation' in props ? props.setLocation : undefined;
  const setWatchLocation =
    'setWatchLocation' in props ? props.setWatchLocation : undefined;

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<Search | null>(null);

  const [findLocationActive, setFindLocationActive] = useState(false);
  const [femaRegions, setFemaRegions] = useState<FemaRegionPolygon[] | null>(
    null
  );
  const [femaRegionError, setFemaRegionError] = useState(false);

  const updateLocationFromCoordinates = async ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    if (!setLocation) return;

    const geometry: Geometry = {
      type: 'Point',
      coordinates: [longitude, latitude],
    };

    const centroidFeature: Feature = {
      type: 'Feature',
      geometry,
      properties: {},
    };

    const { address, placeName } = await reverseGeocode(longitude, latitude);

    const newGeoLocation = buildGeoLocationFromGeoJson({
      geometry,
      centroid: centroidFeature,
      address,
      placeName,
      existingId: location?.id,
    });

    setWatchLocation?.(true);
    setLocation(newGeoLocation);
    drawLocationOnMap(geometry, newGeoLocation.name);
  };

  const { mapView } = useLocalArcgisMapView({
    isActive: findLocationActive,
    containerRef: mapContainerRef,
    basemap: HYBRID,
    initialView: US_INITIAL_VIEW,
    onMapReady: (readyMapView) => {
      drawAndMaybeZoom(readyMapView);
    },
  });

  const reverseGeocode = useReverseGeocode();
  const getLocationFromArcgisGeometry =
    useLocationFromArcgisGeometry(reverseGeocode);

  const {
    drawingLayerRef: drawLayerRef,
    sketchViewModelRef,
    clearDrawingLayer: clearMapGraphics,
  } = useArcgisSketchLayer({
    mapView: findLocationActive && !readOnly ? mapView : null,
    layerTitle: 'Geolocation',
    onCreateComplete: async ({ geometry }) => {
      if (!setLocation) return;

      const newGeoLocation = await getLocationFromArcgisGeometry(geometry);
      if (!newGeoLocation) return;

      setWatchLocation?.(true);
      setLocation(newGeoLocation);
      drawLocationOnMap(
        newGeoLocation.geojson.data.features[0].geometry,
        newGeoLocation.address || undefined
      );
    },
  });

  const { drawLocationOnMap } = useLocationGraphicLayer({
    mapView,
    drawingLayerRef: drawLayerRef,
    clearDrawingLayer: clearMapGraphics,
  });

  const drawAndMaybeZoom = useCallback(
    (targetMapView: __esri.MapView | null) => {
      if (!targetMapView || !location?.geojson?.data?.features?.[0]) return;
      const geometry = location.geojson.data.features[0].geometry;

      drawLocationOnMap(geometry, location.address || undefined, targetMapView);

      const graphic = drawLayerRef.current.graphics.getItemAt(0);
      if (!graphic) return;

      zoomMapViewToGraphic({ mapView: targetMapView, graphic });
    },
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    [drawLocationOnMap, drawLayerRef, location]
  );

  const handleSearchSelectResult = useCallback(
    async (event: __esri.SearchSelectResultEvent) => {
      if (!setLocation || !event.result.feature?.geometry) return;

      const newGeoLocation = await getLocationFromArcgisGeometry(
        event.result.feature.geometry,
        event.result.name
      );
      if (!newGeoLocation) return;

      setWatchLocation?.(true);
      setLocation(newGeoLocation);
      drawLocationOnMap(
        newGeoLocation.geojson.data.features[0].geometry,
        newGeoLocation.address || event.result.name
      );
    },
    [
      drawLocationOnMap,
      setLocation,
      setWatchLocation,
      getLocationFromArcgisGeometry,
    ]
  );

  useMapSearchWidget({
    mapView,
    isActive: findLocationActive,
    searchRef,
    onSelectResult: handleSearchSelectResult,
  });

  useEffect(() => {
    if (!showFemaRegionMenu || !findLocationActive) return;
    let cancelled = false;
    loadFemaRegionPolygons()
      .then((regions) => {
        if (!cancelled) setFemaRegions(regions);
      })
      .catch(() => {
        if (!cancelled) setFemaRegionError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [showFemaRegionMenu, findLocationActive]);

  const applyFemaRegion = async (regionNumber: number) => {
    const region = femaRegions?.find(
      (entry) => entry.regionNumber === regionNumber
    );
    if (!region || !setLocation) return;

    const centroid = turf.centroid(region.geometry);
    const [longitude, latitude] = centroid.geometry.coordinates as [
      number,
      number,
    ];
    const { address } = await reverseGeocode(longitude, latitude);
    const newGeoLocation = buildGeoLocationFromGeoJson({
      geometry: region.geometry,
      centroid,
      address: address || region.label,
      placeName: region.label,
      existingId: location?.id,
    });

    setWatchLocation?.(true);
    setLocation(newGeoLocation);
    drawLocationOnMap(region.geometry, region.label);

    const graphic = drawLayerRef.current.graphics.getItemAt(0);
    if (graphic && mapView) {
      zoomMapViewToGraphic({ mapView, graphic });
    }
  };

  const onDrawPoint = () => {
    sketchViewModelRef.current?.create('point');
  };

  const onDrawPolygon = () => {
    sketchViewModelRef.current?.create('polygon');
  };

  const onClear = () => {
    clearMapGraphics();
    setWatchLocation?.(true);
  };

  const { onImportFileChange } = useGeoJsonImport({
    setLocation,
    setWatchLocation,
    reverseGeocode,
    drawLocationOnMap,
    drawingLayerRef: drawLayerRef,
    mapView,
  });

  const openButtonLabel = location
    ? hideSelectLocationButton
      ? 'View Geolocation'
      : 'View/Edit Geolocation'
    : hideSelectLocationButton
      ? 'View Geolocation'
      : 'Edit Geolocation';

  return (
    <div className="tw:w-full">
      {!location ? (
        <div className="tw:mb-3">
          {findLocationActive ? (
            <Button
              variant="outline"
              onClick={() => setFindLocationActive(false)}
              type="button"
            >
              Cancel Geolocation
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setFindLocationActive(true)}
              type="button"
            >
              {openButtonLabel}
            </Button>
          )}
        </div>
      ) : (
        <div className="tw:mb-3">
          <LocationSummary
            location={location}
            readOnly={readOnly}
            onSaveName={(name) => {
              if (setLocation) {
                setWatchLocation?.(true);
                setLocation({ ...location, name });
              }
            }}
            onSaveAddress={(address) => {
              if (setLocation) {
                setWatchLocation?.(true);
                setLocation({ ...location, address });
              }
            }}
          />

          <div className="tw:mt-2">
            {findLocationActive ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setFindLocationActive(false)}
              >
                Accept Geolocation
              </Button>
            ) : !hideSelectLocationButton ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setFindLocationActive(true)}
              >
                {openButtonLabel}
              </Button>
            ) : null}
          </div>
        </div>
      )}

      {findLocationActive && (
        <>
          {!hideSelectLocationButton && (
            <LatLngInputPair
              centroid={location?.centroid}
              onChange={updateLocationFromCoordinates}
              readOnly={readOnly}
            />
          )}
          {showFemaRegionMenu && !readOnly && setLocation && (
            <div className="tw:mt-3 tw:space-y-2">
              <Label>FEMA region</Label>
              {femaRegionError ? (
                <p className="tw:text-xs tw:text-muted-foreground">
                  FEMA regions could not be loaded.
                </p>
              ) : (
                <SearchableSelect
                  aria-label="FEMA region"
                  options={(femaRegions ?? []).map((region) => ({
                    label: region.label,
                    value: String(region.regionNumber),
                  }))}
                  value={null}
                  onChange={(next) => {
                    if (!next) return;
                    void applyFemaRegion(Number(next.value));
                  }}
                  placeholder={
                    femaRegions
                      ? 'Select a FEMA region'
                      : 'Loading FEMA regions...'
                  }
                  disabled={!femaRegions}
                />
              )}
            </div>
          )}
          <div className="tw:mt-3 tw:flex tw:w-full tw:flex-col tw:gap-2">
            <div
              className="tw:relative tw:w-full tw:overflow-hidden tw:rounded-md tw:border tw:border-border"
              style={{ height: '400px' }}
            >
              <div ref={mapContainerRef} className="tw:h-full tw:w-full" />
              {!readOnly && !hideSelectLocationButton && (
                <MapToolPalette
                  onDrawPoint={onDrawPoint}
                  onDrawPolygon={onDrawPolygon}
                  onClear={onClear}
                  onImportFileChange={onImportFileChange}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GeoLocationComponent;
